import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { authService } from '@/services/auth'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/services/api'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login: storeLogin } = useAuthStore()

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [mobile, setMobile] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password', { replace: true })
    }
  }, [token, navigate])

  const passwordChecks = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', ok: /[a-z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One special character', ok: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) },
  ]
  const passwordValid = passwordChecks.every((c) => c.ok)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setError(null)

    if (password !== passwordConfirm) {
      setError('Passwords do not match.')
      return
    }
    if (!passwordValid) {
      setError('Please meet all password requirements.')
      return
    }
    if (!mobile.trim()) {
      setError('Please enter your mobile number.')
      return
    }

    setIsLoading(true)
    try {
      const res = await authService.resetPasswordWithToken(token, {
        new_password: password,
        new_password_confirm: passwordConfirm,
        new_mobile: mobile.trim(),
      })
      storeLogin(res.access_token, res.refresh_token, res.user)
      setSuccess(true)
      setTimeout(() => navigate('/account'), 2500)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">All done!</h2>
          <p className="text-gray-600">Your password and mobile number have been updated. Taking you to your account...</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Set Your Password</h1>
          <p className="mt-2 text-sm text-gray-600">
            Set a new password and confirm your mobile number to complete your account setup.
          </p>
        </div>

        <Card className="p-6 sm:p-8">
          {error && <Alert variant="error" message={error} onClose={() => setError(null)} className="mb-4" />}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New password */}
            <div className="relative">
              <Input
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password requirements */}
            {password && (
              <ul className="space-y-1 text-sm">
                {passwordChecks.map((c) => (
                  <li key={c.label} className={`flex items-center gap-2 ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>
                    {c.ok
                      ? <CheckCircle size={14} />
                      : <AlertCircle size={14} />}
                    {c.label}
                  </li>
                ))}
              </ul>
            )}

            {/* Confirm password */}
            <div className="relative">
              <Input
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordConfirm && password !== passwordConfirm && (
              <p className="text-sm text-red-600">Passwords do not match.</p>
            )}

            {/* Mobile number */}
            <Input
              label="Your Current Mobile Number"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="e.g. 07700 900000"
              autoComplete="tel"
              required
            />
            <p className="text-xs text-gray-500 -mt-2">
              This will be used for future login verification codes.
            </p>

            <Button
              type="submit"
              fullWidth
              isLoading={isLoading}
              disabled={!passwordValid || password !== passwordConfirm || !mobile.trim()}
            >
              Set Password &amp; Continue
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-gray-500">
            Need a new link?{' '}
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700">
              Request one here
            </Link>
          </p>
        </Card>
      </div>
    </div>
  )
}
